import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Mail, Play } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Reveal from '../components/Reveal';
import SEO from '../components/SEO';
import { organization, programs } from '../data/siteData';
import { assetPath } from '../utils/assetPath';

const programGalleries = {
  rehabilitacion: [
    assetPath('/assets/photos/504132640_3056995067797229_5703131928566182799_n.jpg'),
    assetPath('/assets/photos/504295535_3056995087797227_3118181787785873911_n.jpg'),
    assetPath('/assets/photos/482004702_1040535661453717_1902875297155302169_n.jpg'),
  ],
  valores: [
    assetPath('/assets/photos/537687572_1192205376285480_5362959226390351494_n.jpg'),
    assetPath('/assets/photos/537123962_1192205412952143_8381438350100508549_n.jpg'),
    assetPath('/assets/photos/WhatsApp Image 2025-04-01 at 3.56.06 PM.jpeg'),
  ],
  becas: [
    assetPath('/assets/photos/481770997_1040536064787010_7293146809642141697_n.jpg'),
    assetPath('/assets/photos/504358165_3058480867648649_6609700657505944187_n.jpg'),
    assetPath('/assets/photos/539082516_1192205312952153_3053428314392908149_n.jpg'),
  ],
};

function ProgramMiniCard({ program }) {
  const Icon = program.icon;
  return (
    <Link className={`program-mini program-mini--${program.theme}`} to={program.path}>
      <span><Icon /></span>
      <div><small>{program.eyebrow}</small><strong>{program.shortTitle}</strong></div>
      <ArrowRight />
    </Link>
  );
}

export default function ProgramPage() {
  const { slug } = useParams();
  const program = programs.find((item) => item.path.endsWith(slug));
  if (!program) return <Navigate to="/" replace />;

  const Icon = program.icon;
  const otherPrograms = programs.filter((item) => item.id !== program.id);
  const isScholarship = program.id === 'becas';
  const gallery = programGalleries[program.id];
  const banner = isScholarship
    ? assetPath('/assets/banners/Leaderboard BECAS.png')
    : program.id === 'valores'
      ? assetPath('/assets/banners/Leaderboard SE TU AMIGO.png')
      : null;

  const faq = [
    {
      question: `¿Cómo solicito información sobre ${program.shortTitle}?`,
      answer: `Escríbenos a ${program.email} o utiliza el formulario de contacto seleccionando este programa.`,
    },
    {
      question: '¿Dónde opera Fundación Tony?',
      answer: 'La Fundación desarrolla acciones en Veracruz y Boca del Río, además de iniciativas educativas con alcance nacional.',
    },
  ];

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: program.title,
      description: program.intro,
      provider: { '@type': 'NGO', name: organization.name },
      areaServed: 'México',
      url: `${import.meta.env.VITE_SITE_URL || 'https://fundaciontony.com.mx'}${program.path}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
  ];

  return (
    <main id="contenido" className={`program-page theme-${program.theme}`} style={{ '--program': program.color, '--program-secondary': program.secondary }}>
      <SEO
        title={`${program.title} | Fundación Tony`}
        description={program.intro}
        path={program.path}
        image={program.image}
        schema={schema}
      />

      <section className="program-hero">
        <div className="program-hero__pattern" aria-hidden="true" />
        <div className="shell program-hero__grid">
          <div className="program-hero__copy">
            <Link className="breadcrumb" to="/"><ArrowLeft /> Inicio / Programas</Link>
            <span className="program-kicker"><Icon /> {program.eyebrow}</span>
            <h1>{program.title}</h1>
            <p>{program.intro}</p>
            <div className="program-hero__actions">
              <Link className="button button--light" to={`/contacto?programa=${program.id}`}>Solicitar información <ArrowRight /></Link>
              {isScholarship && <a className="button button--ghost-light" href={organization.scholarships} target="_blank" rel="noreferrer">Ver convocatoria <ArrowUpRight /></a>}
            </div>
          </div>
          <div className="program-hero__visual">
            <img src={program.image} alt={`Actividad del programa ${program.title}`} />
            <div className="program-hero__metric"><strong>{program.metric}</strong><span>{program.metricLabel}</span></div>
          </div>
        </div>
      </section>

      {banner && (
        <div className="shell program-banner-wrap">
          <img className="program-banner" src={banner} alt={`Identidad gráfica de ${program.title}`} />
        </div>
      )}

      <section className="program-story section">
        <div className="shell program-story__grid">
          <Reveal>
            <span className="eyebrow" style={{ color: program.color }}>Nuestro propósito</span>
            <h2>{program.storyTitle || program.quote}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            {program.id === 'rehabilitacion' && (
              <>
                <p>El Programa de Becas de Rehabilitación facilita tratamiento especializado a personas que no podrían cubrirlo por sus propios medios. La colaboración con el Centro Estatal Contra las Adicciones (CÚSPIDE) permite sumar experiencia clínica y acompañamiento institucional.</p>
                <p>El proceso comienza con información para la familia, continúa con una sesión clínica de referencia y, cuando existen condiciones de ingreso, se formalizan la canalización y la beca. Entre enero y junio de 2026 se otorgaron seis apoyos.</p>
              </>
            )}
            {program.id === 'valores' && (
              <>
                <p>“Sé tu amigo, vive con valores” trabaja la prevención desde antes del riesgo. Propone que niñas, niños, adolescentes y jóvenes reconozcan en los valores una herramienta práctica para cuidar su integridad física, psicológica y social.</p>
                <p>El programa capacita instructores, organiza pláticas en planteles de Veracruz y Boca del Río y se vincula con universitarios mediante servicio social y prácticas profesionales. También participa en Escuela de Padres, donde en promedio se forman 45 docentes al año.</p>
              </>
            )}
            {isScholarship && (
              <>
                <p>El programa nació para que la falta de recursos no interrumpa la trayectoria de jóvenes universitarios con excelencia académica y un firme compromiso con su formación profesional.</p>
                <p>Más allá del apoyo económico, impulsa permanencia escolar, movilidad social, liderazgo y participación comunitaria. Honra la visión humanista de Antonio Chedraui Mafud, quien entendió la educación como un medio para transformar vidas y construir un mejor futuro.</p>
              </>
            )}
          </Reveal>
        </div>
      </section>

      <section className="program-process section">
        <div className="shell">
          <Reveal className="section-heading section-heading--center">
            <span className="eyebrow" style={{ color: program.color }}>Cómo genera impacto</span>
            <h2>Así se convierte una intención en acompañamiento.</h2>
            <p>Cada etapa responde a una necesidad concreta y ayuda a que el apoyo llegue de forma clara y responsable.</p>
          </Reveal>
          <div className="process-grid">
            {program.details.map((detail, index) => {
              const DetailIcon = detail.icon;
              return (
                <Reveal className="process-card" key={detail.title} delay={index * 0.07}>
                  <span className="process-card__icon"><DetailIcon /></span>
                  <h3>{detail.title}</h3>
                  <p>{detail.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="program-gallery section">
        <div className="shell gallery-grid">
          <Reveal className="gallery-grid__title">
            <span className="eyebrow" style={{ color: program.color }}>Historias en acción</span>
            <h2>El impacto también tiene rostros, lugares y momentos.</h2>
            <p>Cada taller, apoyo y alianza deja una historia que merece ser vista y compartida.</p>
          </Reveal>
          {gallery.map((image, index) => (
            <Reveal className={`gallery-image gallery-image--${index + 1}`} key={image} delay={index * 0.08}>
              <img src={image} alt={`Actividad vinculada con ${program.shortTitle}`} loading="lazy" />
            </Reveal>
          ))}
        </div>
      </section>

      {isScholarship && (
        <section className="video-story section">
          <div className="shell video-story__grid">
            <Reveal className="video-story__copy">
              <span className="eyebrow" style={{ color: program.color }}>Una historia de gratitud</span>
              <h2>Dibujando un mañana</h2>
              <p>El apoyo educativo cobra sentido cuando escuchamos la voz de quienes convierten una beca en nuevas posibilidades.</p>
              <a className="text-link" href={organization.scholarships} target="_blank" rel="noreferrer">Consultar convocatoria <ArrowUpRight /></a>
            </Reveal>
            <Reveal className="video-story__player" delay={0.1}>
              <video controls preload="metadata" poster={assetPath('/assets/video/poster-becas.jpg')}>
                <source src={assetPath('/assets/video/dibujando-un-manana.mp4')} type="video/mp4" />
                Tu navegador no puede reproducir este video.
              </video>
              <span><Play fill="currentColor" /> Testimonio de Becas Tony Chedraui Mafud</span>
            </Reveal>
          </div>
        </section>
      )}

      <section className="program-contact">
        <div className="shell program-contact__grid">
          <div>
            <span className="eyebrow eyebrow--light">¿Necesitas orientación?</span>
            <h2>Conversemos sobre este programa.</h2>
            <p>Nuestro equipo te ayudará a identificar la información y el canal adecuado.</p>
          </div>
          <div className="program-contact__actions">
            <a className="program-email" href={`mailto:${program.email}`}><Mail /> <span><small>Correo del programa</small>{program.email}</span></a>
            <Link className="button button--light" to={`/contacto?programa=${program.id}`}>Abrir formulario <ArrowRight /></Link>
          </div>
        </div>
      </section>

      <section className="faq section">
        <div className="shell faq__grid">
          <Reveal><span className="eyebrow" style={{ color: program.color }}>Preguntas frecuentes</span><h2>Información esencial</h2></Reveal>
          <div>
            {faq.map((item) => (
              <Reveal className="faq-item" key={item.question}>
                <span><Check /></span><div><h3>{item.question}</h3><p>{item.answer}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="more-programs section">
        <div className="shell">
          <Reveal className="section-heading"><span className="eyebrow eyebrow--blue">Sigue explorando</span><h2>Conoce nuestros otros programas</h2></Reveal>
          <div className="program-mini-grid">{otherPrograms.map((item) => <ProgramMiniCard key={item.id} program={item} />)}</div>
        </div>
      </section>
    </main>
  );
}
